import type { DistributionPoint } from "../components/SalesProductAnalyticsTabsDashboard.data";

export type SalesByChannelChartRow = {
  label: string;
  value: number;
  /** Share of basis (total revenue, gross revenue, tax pool, etc.), 0–100 */
  shareOfTotal: number;
  /** 1 = largest bar value */
  rank: number;
  /** Carried for tooltip (avoids inline Tooltip renderers). */
  currencyLabel: string;
  /** Signed / source amount when `value` is an absolute magnitude for the bar. */
  displayAmount?: number;
  /** Pre-formatted amount from the API (e.g. "USD 2,006,675"), preferred over formatting `value`/`displayAmount` locally. */
  formattedValue?: string;
};

export type SalesByChannelChartModel = {
  rows: SalesByChannelChartRow[];
  totalRevenue: number;
  channelCount: number;
  /** Share % of the top row vs basis */
  top1SharePct: number;
};

export type BuildSalesByChannelChartModelOptions = {
  /** Bar length uses |raw value|; tooltip uses signed `displayAmount`. */
  absoluteBar?: boolean;
  /** When set, share % = bar / denominator × 100 (e.g. gross revenue). */
  shareDenominator?: number;
  /** Left summary number; defaults to sum of bar magnitudes. */
  summaryTotal?: number;
};

const clampPct = (n: number) => Math.round(Math.min(100, Math.max(0, n)) * 10) / 10;

/**
 * Sorts rows by bar value descending (Recharts vertical layout: first row = top).
 * Rank 1 = largest magnitude.
 */
export function buildSalesByChannelChartModel(
  channels: readonly DistributionPoint[],
  currencyLabel = "PKR",
  options?: BuildSalesByChannelChartModelOptions,
): SalesByChannelChartModel {
  const sorted = [...channels]
    .filter(c => c.label.trim().length > 0)
    .map(c => {
      const raw = Number.isFinite(c.value) ? c.value : 0;
      const bar = options?.absoluteBar ? Math.abs(raw) : Math.max(0, raw);
      return { label: c.label, raw, bar, formattedValue: c.formattedValue };
    })
    .sort((a, b) => b.bar - a.bar);

  const sumBars = sorted.reduce((s, c) => s + c.bar, 0);
  const denom =
    options?.shareDenominator !== undefined && options.shareDenominator > 0
      ? options.shareDenominator
      : sumBars;

  const rows: SalesByChannelChartRow[] = sorted.map((c, i) => ({
    label: c.label,
    value: c.bar,
    shareOfTotal: denom > 0 ? clampPct((c.bar / denom) * 100) : 0,
    rank: i + 1,
    currencyLabel,
    displayAmount: options?.absoluteBar ? c.raw : undefined,
    formattedValue: c.formattedValue,
  }));

  const displayTotal =
    options?.summaryTotal !== undefined && Number.isFinite(options.summaryTotal)
      ? options.summaryTotal
      : sumBars;

  const leader = rows[0];
  const top1SharePct =
    leader && denom > 0 ? clampPct((leader.value / denom) * 100) : 0;

  return {
    rows,
    totalRevenue: displayTotal,
    channelCount: rows.length,
    top1SharePct,
  };
}
/** Axis / bar-end labels — short PKR */
export function formatPkrCompact(value: number, currencyLabel = "PKR"): string {
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (!Number.isFinite(abs) || abs === 0) {
    return `${currencyLabel} 0`;
  }
  if (abs >= 1_000_000_000) {
    return `${sign}${currencyLabel} ${(abs / 1_000_000_000).toFixed(1)}B`;
  }
  if (abs >= 1_000_000) {
    return `${sign}${currencyLabel} ${(abs / 1_000_000).toFixed(2)}M`;
  }
  if (abs >= 1_000) {
    return `${sign}${currencyLabel} ${Math.round(abs / 1_000)}k`;
  }
  return `${sign}${currencyLabel} ${Math.round(abs).toLocaleString()}`;
}

/** Tooltip / tables — full grouping */
export function formatPkrDetailed(value: number, currencyLabel = "PKR"): string {
  if (!Number.isFinite(value)) {
    return `${currencyLabel} —`;
  }
  const rounded = Math.round(value);
  return `${currencyLabel} ${rounded.toLocaleString()}`;
}

